import React from "react";
import Flagship from "../../core/flagship";
import { empty, arraysEqual, shouldShowField } from "../../utils/helpers";
import "../../styles/field.scss";
import { Label, Icon, Button, Segment } from "semantic-ui-react";
import moment from "moment-business-days";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Field from "./Field";
import { connect } from "react-redux";

const DATE_FORMAT = "MM/DD/YYYY";
const fmtDate = (d) => (d ? moment(d).format(DATE_FORMAT) : "");

/** Pure helper: compute the fields array from props (no side effects) */
function computeFieldsFromProps(props) {
  const fields = [];
  if (
    (typeof props.social === "undefined" || props.social === false) &&
    (typeof props.email === "undefined" || props.email === false) &&
    (typeof props.callrail === "undefined" || props.callrail === false) &&
    props.recordType === "IO"
  ) {
    fields.push({
      label: "Goal",
      type: "text",
      required: 1,
      active: 1,
    });
  }

  if (
    typeof props.email === "undefined" ||
    props.email === false ||
    props.recordType === "IO"
  ) {
    fields.push({
      label: "Retail Budget",
      type: "text",
      validation: "money",
      required: 1,
      active: 1,
    });
  }

  if (typeof props.recordType !== "undefined" && props.recordType === "IO") {
    fields.push({
      label: "COGS",
      type: "text",
      validation: "money",
      required: 1,
      active: 1,
    });
  }

  return fields;
}

/** Pure helper: build transient inputs map from value[] */
function buildInputsFromValue(valueArr) {
  const inputs = {};
  (valueArr || []).forEach((flight, i) => {
    const start = fmtDate(flight && flight.startDate ? flight.startDate : null);
    const end = fmtDate(flight && flight.endDate ? flight.endDate : null);
    inputs[i] = { start, end };
  });
  return inputs;
}

class FlightField extends React.Component {
  /** ---- required by eslint react/sort-comp: static before constructor ---- */
  static getDerivedStateFromProps(nextProps, prevState) {
    let needsUpdate = false;
    const nextState = {};

    // Sync when value prop ref changes
    if (nextProps.value !== prevState.prevValuePropRef) {
      const nextValue =
        !empty(nextProps.value) && !arraysEqual(nextProps.value, [""])
          ? nextProps.value
          : prevState.value;

      if (nextValue !== prevState.value) {
        nextState.value = nextValue;
        nextState.instances = nextValue.length;
        nextState.inputs = buildInputsFromValue(nextValue);
      }
      nextState.prevValuePropRef = nextProps.value;
      needsUpdate = true;
    }

    // Recompute fields when key props change
    const fieldKey = `${!!nextProps.social}|${!!nextProps.email}|${!!nextProps.callrail}|${nextProps.recordType}`;
    if (fieldKey !== prevState.prevFieldKey) {
      nextState.fields = computeFieldsFromProps(nextProps);
      nextState.prevFieldKey = fieldKey;
      needsUpdate = true;
    }

    return needsUpdate ? nextState : null;
  }

  constructor(props, context) {
    super(props, context);

    this.state = this.getInitialState();
    this.deleteFlight = this.deleteFlight.bind(this);
    this.addFlight = this.addFlight.bind(this);
    this.checkRequiredSubFields = this.checkRequiredSubFields.bind(this);
    this.checkRequired = this.checkRequired.bind(this);
    this.onAnimationEnd = this.onAnimationEnd.bind(this);
    this.updateInstance = this.updateInstance.bind(this);
    this.setInputText = this.setInputText.bind(this);
  }

  /* ---- keep getInitialState before componentDidMount ---- */
  getInitialState() {
    let initialValue = this.getInitialValue();
    if (!empty(this.props.value) && !arraysEqual(this.props.value, [""])) {
      initialValue = this.props.value;
    }

    const prevFieldKey = `${!!this.props.social}|${!!this.props.email}|${!!this.props.callrail}|${this.props.recordType}`;

    return {
      // data
      value: initialValue,
      instances: initialValue.length,
      inputs: buildInputsFromValue(initialValue),
      fields: computeFieldsFromProps(this.props),

      // refs for getDerivedStateFromProps comparisons
      prevValuePropRef: this.props.value,
      prevFieldKey,

      // misc ui
      focused: false,
      errors: false,
      errorMessage: "",
      fieldData: null,
    };
  }

  componentDidMount() {
    this.checkRequired();
  }

  /* ---- eslint wants helpers after lifecycle methods ---- */
  getInitialValue() {
    return [
      {
        startDate: "",
        endDate: "",
        newFlight: false,
      },
    ];
  }

  // Update a single flight object immutably
  updateInstance(index, patch, opts) {
    const options = opts || {};
    const triggerChange = options.triggerChange === true;
    const value = [...this.state.value];
    value[index] = { ...(value[index] || {}), ...patch };
    this.setState({ value }, this.checkRequired);
    if (triggerChange && typeof this.props.onChange === "function") {
      this.props.onChange(null, value);
    }
  }

  // Update transient input text (does NOT touch value)
  setInputText(index, which, text) {
    const inputs = { ...(this.state.inputs || {}) };
    const entry = { ...(inputs[index] || {}) };
    entry[which] = text;
    inputs[index] = entry;
    this.setState({ inputs });
  }

  deleteFlight(e, instance) {
    let newValue = [...this.state.value];
    newValue.splice(instance, 1);

    this.setState(
      {
        value: newValue,
        instances: newValue.length,
        inputs: buildInputsFromValue(newValue),
      },
      this.checkRequired
    );

    this.props.onChange(e, newValue);
  }

  addFlight() {
    let newValue = [...this.state.value];
    let lastFlight = newValue[newValue.length - 1];

    if (!this.props.email) {
      const startDate = moment(lastFlight.startDate).add(1, "month").startOf("month");
      const endDate = moment(lastFlight.endDate).add(1, "month").endOf("month").startOf("day");
      newValue.push({
        startDate,
        endDate,
        newFlight: true,
      });
    } else {
      const startDate = moment(lastFlight.startDate).add(1, "month").startOf("month");
      newValue.push({
        startDate,
        newFlight: true,
      });
    }

    this.setState(
      {
        value: newValue,
        instances: newValue.length,
        inputs: buildInputsFromValue(newValue),
      },
      this.checkRequired
    );
  }

  onAnimationEnd() {
    const newValue = this.state.value.map((flight) => ({ ...flight, newFlight: false }));
    this.setState({ value: newValue });
  }

  duplicateFlight(index) {
    let newValue = [...this.state.value];
    let isEmail = this.props.email;
    let duplicateFlight = Object.assign({}, newValue[index]);
    newValue.splice(index + 1, 0, duplicateFlight);
    newValue.map((flight, i) => {
      if (
        i > index &&
        moment(flight.startDate).format("MM-YY") ===
          moment(newValue[i - 1].startDate).format("MM-YY")
      ) {
        flight.startDate = moment(flight.startDate).add(1, "month").startOf("month");

        if (isEmail !== true) {
          flight.endDate = moment(flight.endDate).add(1, "month").endOf("month").startOf("day");
          flight.newFlight = i === index + 1;
        }
      }
    });

    if (typeof this.props.onChange === "function") {
      this.props.onChange(null, newValue);
    }

    this.setState(
      {
        value: newValue,
        instances: newValue.length,
        inputs: buildInputsFromValue(newValue),
      },
      this.checkRequired
    );
  }

  onChange(e, data, subField, instance) {
    let newValue = [...this.state.value];

    if (typeof newValue[instance] === "object") {
      let thisFlight = Object.assign({}, newValue[instance]);
      thisFlight[subField] = data;

      newValue[instance] = thisFlight;
      this.setState({ value: newValue }, this.checkRequired);
    }

    if (typeof this.props.onChange === "function") {
      this.props.onChange(e, newValue);
    }
  }

  checkRequired() {
    if (typeof this.props.setFlightSubFieldsMissing === "function") {
      this.props.setFlightSubFieldsMissing(this.checkRequiredSubFields());
    }
  }

  checkRequiredSubFields() {
    let subFieldsMissing = false;
    let value = [...this.state.value];
    let recordContext = this.props.recordContext;
    let isEmail = this.props.email;
    let self = this;

    this.state.fields.map(function (field) {
      if (field.required === true || field.required === 1) {
        value.map(function (instance) {
          if (
            instance[field.label] === "" ||
            instance[field.label] === null ||
            typeof instance[field.label] === "undefined"
          ) {
            subFieldsMissing = true;
          }
        });
      }
    });

    value.map(function (instance, i) {
      if (
        instance["startDate"] === "" ||
        instance["startDate"] === null ||
        typeof instance["startDate"] === "undefined" ||
        (isEmail !== true &&
          (instance["endDate"] === "" ||
            instance["endDate"] === null ||
            typeof instance["endDate"] === "undefined"))
      ) {
        subFieldsMissing = true;
      }
      if (
        typeof recordContext !== "undefined" &&
        recordContext === "add" &&
        !empty(instance["startDate"]) &&
        self.props.userRole !== "admin" &&
        !moment(instance["startDate"]).isAfter(moment().businessAdd(2, "days"))
      ) {
        Flagship.notify(
          "All flights require a minimum of 3 business days before starting. Please update Flight #" +
            (i + 1) +
            ".",
          "error"
        );
        instance["startDate"] = "";
      }
    });
    this.setState({ value });
    return subFieldsMissing;
  }

  render() {
    let self = this;

    return (
      <div className="all-flights-container">
        {Array.apply(null, { length: this.state.instances }).map(function (b, key) {
          let instance = {
            startDate: null,
            endDate: null,
          };

          if (
            self.state.value !== null &&
            !empty(self.state.value[key]) &&
            !empty(self.state.value[key].endDate)
          ) {
            instance.endDate = moment(self.state.value[key].endDate);
          }

          if (
            self.state.value !== null &&
            !empty(self.state.value[key]) &&
            !empty(self.state.value[key].startDate)
          ) {
            instance.startDate = moment(self.state.value[key].startDate);
          }

          let flightLabel =
            self.props.email
              ? "Deployment"
              : self.state.instances > 1
              ? "Flight #" + (key + 1)
              : "Campaign";

          // Visible text for each input: use transient inputs map, fallback to formatted date
          const inputEntry = (self.state.inputs && self.state.inputs[key]) || {};
          const startInputText =
            typeof inputEntry.start !== "undefined" ? inputEntry.start : fmtDate(instance.startDate);
          const endInputText =
            typeof inputEntry.end !== "undefined" ? inputEntry.end : fmtDate(instance.endDate);

          const startMinDate =
            self.props.userRole !== "admin" ? moment().businessAdd(3, "days") : null;

          // Commit typed value on blur/Enter (valid date string only)
          const commitTyped = (e, field) => {
            const raw = (e && e.target ? e.target.value : "").trim();
            if (!raw) return; // keep as-is on empty
            const m = moment(raw, DATE_FORMAT, true);
            if (!m.isValid()) return; // leave text; don't update selected

            // Your existing pathway so rules/state updates apply
            self.onChange(e, m, field, key);

            // Keep visible text in sync with what we accepted (transient only)
            if (field === "startDate") {
              self.setInputText(key, "start", fmtDate(m));
            } else {
              self.setInputText(key, "end", fmtDate(m));
            }
          };

          return (
            <div
              className={
                self.state.value[key].newFlight
                  ? "single-flight-container new-flight"
                  : "single-flight-container"
              }
              onAnimationEnd={self.onAnimationEnd}
              key={key}
            >
              <Segment
                className="single-flight no-move single-flight-heading"
                attached="top"
                inverted
                color={self.state.value[key].newFlight ? false : "green"}
              >
                <div className="field-container clearfix">
                  <div className="ui input single-flight-fields">
                    <Label className="single-flight-label" color="green">
                      {flightLabel}
                    </Label>
                    {self.state.instances > 1 ? (
                      <Icon
                        className="delete-flight"
                        name="delete"
                        circular
                        link
                        inverted
                        size="small"
                        onClick={(e) => self.deleteFlight(e, key)}
                      />
                    ) : null}
                    {!self.props.email ? (
                      <Icon
                        className="duplicate-flight"
                        name="copy"
                        circular
                        link
                        inverted
                        size="small"
                        onClick={() => self.duplicateFlight(key)}
                      />
                    ) : null}

                    <div className="flight-date-range">
                      <div className="flagship-single-field field-type-date">
                        <DatePicker
                          key={
                            instance.startDate
                              ? `start-${instance.startDate.valueOf()}`
                              : "start-empty"
                          }
                          selected={instance.startDate}
                          placeholderText={!self.props.email ? "Start Date*" : "Send Date*"}
                          selectsStart
                          startDate={instance.startDate}
                          endDate={instance.endDate}
                          minDate={startMinDate}
                          dateFormat={DATE_FORMAT}
                          value={startInputText}
                          onChangeRaw={(e) => {
                            // let the user type anything; store it as text only (transient)
                            self.setInputText(key, "start", e.target.value);
                            // stop internal parsing/preSelection (prevents NaN calendars)
                            if (e && typeof e.preventDefault === "function") e.preventDefault();
                          }}
                          onBlur={(e) => commitTyped(e, "startDate")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitTyped(e, "startDate");
                          }}
                          onChange={(value, e) => {
                            // Calendar pick
                            self.onChange(e, value, "startDate", key);
                            // Sync visible text (transient)
                            self.setInputText(key, "start", fmtDate(value));
                          }}
                          // ensure calendar opens to a valid month
                          openToDate={
                            instance.startDate ||
                            (self.props.userRole !== "admin"
                              ? moment().businessAdd(3, "days")
                              : moment())
                          }
                        />
                      </div>
                      {!self.props.email ? <span className="date-separator">&mdash;</span> : null}
                      {!self.props.email ? (
                        <div className="flagship-single-field field-type-date">
                          <DatePicker
                            key={
                              instance.endDate
                                ? `end-${instance.endDate.valueOf()}`
                                : "end-empty"
                            }
                            selected={instance.endDate}
                            placeholderText="End Date*"
                            selectsEnd
                            startDate={instance.startDate}
                            endDate={instance.endDate}
                            minDate={!empty(instance.startDate) ? instance.startDate : null}
                            dateFormat={DATE_FORMAT}
                            value={endInputText}
                            onChangeRaw={(e) => {
                              self.setInputText(key, "end", e.target.value);
                              if (e && typeof e.preventDefault === "function") e.preventDefault();
                            }}
                            onBlur={(e) => commitTyped(e, "endDate")}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitTyped(e, "endDate");
                            }}
                            onChange={(value, e) => {
                              self.onChange(e, value, "endDate", key);
                              self.setInputText(key, "end", fmtDate(value));
                            }}
                            openToDate={instance.endDate || instance.startDate || moment()}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Segment>
              <Segment attached className="single-flight no-move" key={key}>
                <div className="field-container clearfix">
                  <div className="ui input single-flight-fields">
                    {self.state.fields.length
                      ? self.state.fields.map(function (item, i) {
                          let label = item.label;

                          let value = "";
                          if (!empty(self.state.value)) {
                            value =
                              self.state.value !== null &&
                              typeof self.state.value[key] !== "undefined" &&
                              self.state.value[key].hasOwnProperty(label)
                                ? self.state.value[key][label]
                                : "";
                          }

                          return shouldShowField(item, value) ? (
                            <Field
                              contentType="flight"
                              model={"flight." + i}
                              key={"field-" + i}
                              value={value}
                              placeholder={item.validation === "money" ? "$" : null}
                              className="flight-sub-field"
                              default={item.default}
                              filterInput={item.validation !== "" ? item.validation : null}
                              onChange={(e, data) => {
                                self.onChange(e, data, label, key);
                              }}
                              {...item}
                            />
                          ) : null;
                        })
                      : null}
                  </div>
                </div>
              </Segment>
            </div>
          );
        })}
        {!this.props.email ? (
          <Button
            onClick={this.addFlight}
            floated="right"
            icon="plus"
            className="add-flight-btn"
            content={"Add a flight..."}
          />
        ) : null}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    userRole: state.main.userRole,
  };
}

export default connect(mapStateToProps, null)(FlightField);



// WEBPACK FOOTER //
// ./src/components/misc/FlightField.js